
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Download, Upload } from "lucide-react";
import { createReagentsFromCSV } from "@/services/reagentRequests/operations/createBulkService";
import { toast } from "sonner";
import { Reagent } from "@/types/reagent.types";

interface CSVUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CSVUploadModal: React.FC<CSVUploadModalProps> = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };
  
  const downloadSampleTemplate = () => {
    const headers = "name,catalogue_number,category_id,description,amount,amount_unit,storage_conditions,number_of_packages\n";
    const sampleData = [
      "Sodium Chloride,S7653,74f0e810-909f-4d5f-8dcf-e5a0e9ea3587,Analytical grade sodium chloride, 99.5% purity,500,g,Room temperature,1",
      "Tris-HCl,T3253,75ce7872-4a0c-4a48-9b6b-d37333f4ca48,1M Tris-HCl solution pH 7.4,1,L,4°C,2"
    ].join("\n");
    
    const content = headers + sampleData;
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reagent_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const parseCSV = (text: string): Array<Partial<Reagent>> => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const headers = lines[0].split(',').map(header => header.trim());
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(value => value.trim());
      const reagent: Record<string, any> = {};
      
      headers.forEach((header, index) => {
        if (values[index]) {
          if (header === 'amount' || header === 'number_of_packages') {
            reagent[header] = Number(values[index]);
          } else {
            reagent[header] = values[index];
          }
        }
      });
      
      return reagent as Partial<Reagent>;
    });
  };
  
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }
    
    if (!file.name.endsWith('.csv')) {
      setError("Please upload a valid CSV file");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const text = await file.text();
      const reagents = parseCSV(text);
      
      if (reagents.length === 0) {
        throw new Error("CSV file is empty or invalid");
      }
      
      console.log("Parsed reagents:", reagents);
      
      const result = await createReagentsFromCSV(reagents);
      
      if (result.success) {
        toast.success(`Successfully uploaded ${result.successCount} reagents!`);
        if (result.errorCount > 0) {
          toast.warning(`${result.errorCount} reagents failed to upload.`);
        }
        onSuccess();
        onClose();
      } else {
        setError(`Failed to upload reagents. ${result.errors[0]}`);
      }
    } catch (err) {
      console.error("Error uploading CSV:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Reagents CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file containing multiple reagents to add them in bulk.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={downloadSampleTemplate}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Download Template
            </Button>
            <span className="text-sm text-muted-foreground">
              Download a sample CSV template to get started
            </span>
          </div>
          
          <div className="grid w-full items-center gap-1.5">
            <label htmlFor="csv-file" className="text-sm font-medium">
              Select CSV File
            </label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            <p className="text-xs text-muted-foreground">
              CSV must include columns for name, description, etc.
            </p>
          </div>
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            disabled={!file || loading} 
            className="flex items-center gap-2"
          >
            {loading ? "Uploading..." : (
              <>
                <Upload size={16} />
                Upload Reagents
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVUploadModal;
